'use client';

import React, { useState } from 'react';
import clsx from 'clsx';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import { mergeTheme, type LinkInBioItem, type LinkInBioProfileView } from '@repo/lib';
import { getThemeCss } from '../CheckoutCSS';
import { getCssDefaultDeclarations } from '../../theming/themePropertyRegistry';
import type { LinkInBioLabels } from '../../lib/linkInBio/getLinkInBioLabels';
import getUrlFromCdn, { CDNFitOption } from '../../services/getUrlFromCdn';
import BadgeIcon, { isBadgeIcon } from '../BadgeIcon';
import PoweredByCheckoutPage from '../PoweredByCheckoutPage';
import SocialIcon from './SocialIcon';

// The store's link in bio route loads no checkout, and the registry defaults point at
// tokens only checkout.css defines (input padding and border width among them).
import '../../theming/checkout.css';

type Props = {
  profile: LinkInBioProfileView;
  items: LinkInBioItem[];
  labels: LinkInBioLabels;
  showBranding?: boolean;
  affiliateToken?: string | null;
  linkTarget?: '_self' | '_blank';
  /** The link in bio's theme, else the store's default theme. Null uses the built-in default. */
  theme?: { scope?: string | null; variables?: unknown } | null;
  /** Where the email sign-up block posts. Left out in the dashboard preview, which only pretends. */
  signup?: { url: string; seller: string } | null;
  ownsDocument?: boolean;
  className?: string;
};

type Palette = {
  backdrop: string;
  background: string;
  text: string;
  mutedText: string;
  buttonBackground: string;
  buttonText: string;
  surfaceButton: string;
  surfaceButtonText: string;
  surface: string;
  surfaceText: string;
  surfaceBorder: string;
};

const LINK_REL = 'nofollow ugc noopener noreferrer';

// The registry defaults sit on :root, where their var() chains resolve against the host page;
// declared again here they resolve against the theme scoped to .cp_link-in-bio.
const scopedDefaults = (scope: string) => `${scope} { ${getCssDefaultDeclarations().join(' ')} }`;

// Safari tints its status bar, toolbar and overscroll from the document's background. The
// breakpoint matches the @2xl container query where the card floats on the backdrop.
const documentBackground = (palette: Palette) => `html { background: ${palette.background}; }
@media (min-width: 42rem) { html { background: ${palette.backdrop}; } }`;

// Values are the theme's own CSS variables, which getThemeCss scopes to the view.
function getPalette(look: LinkInBioProfileView['look']): Palette {
  if (look === 'brand') {
    return {
      backdrop: 'color-mix(in srgb, var(--cp_color-primary) 80%, #000)',
      background: 'var(--cp_color-primary)',
      text: 'var(--cp_store-logo-text-color)',
      mutedText: 'var(--cp_store-logo-text-color)',
      buttonBackground: 'var(--cp_content-background-color)',
      buttonText: 'var(--cp_text-primary)',
      surfaceButton: 'var(--cp_submit-button-background-color)',
      surfaceButtonText: 'var(--cp_submit-button-text-color)',
      surface: 'var(--cp_content-background-color)',
      surfaceText: 'var(--cp_text-primary)',
      surfaceBorder: 'transparent',
    };
  }

  return {
    backdrop: 'color-mix(in srgb, var(--cp_background-color) 88%, var(--cp_text-primary))',
    background: 'var(--cp_background-color)',
    text: 'var(--cp_text-primary)',
    mutedText: 'var(--cp_text-secondary)',
    buttonBackground: 'var(--cp_submit-button-background-color)',
    buttonText: 'var(--cp_submit-button-text-color)',
    surfaceButton: 'var(--cp_submit-button-background-color)',
    surfaceButtonText: 'var(--cp_submit-button-text-color)',
    surface: 'var(--cp_content-background-color)',
    surfaceText: 'var(--cp_text-primary)',
    surfaceBorder: 'var(--cp_border-subtle)',
  };
}

// Cards sit in one stack with the buttons, so they share their corners, but a pill
// radius would turn a tall card into a stadium.
const CARD_RADIUS = {
  borderRadius: 'min(var(--cp_button-border-radius), var(--cp_card-border-radius))',
};
const BUTTON_RADIUS = { borderRadius: 'var(--cp_button-border-radius)' };

// Full class names so Tailwind keeps them.
const ALIGN = {
  left: { items: 'items-start', text: 'text-left', justify: 'justify-start' },
  center: { items: 'items-center', text: 'text-center', justify: 'justify-center' },
  right: { items: 'items-end', text: 'text-right', justify: 'justify-end' },
} as const;

type Align = (typeof ALIGN)[keyof typeof ALIGN];

function buttonPadding(align: LinkInBioProfileView['align'], hasLead: boolean) {
  if (align === 'center') return 'px-14';
  return hasLead ? 'pl-14 pr-4' : 'px-4';
}

function EventLine({
  item,
  labels,
  align,
}: {
  item: Extract<LinkInBioItem, { kind: 'page' }>;
  labels: LinkInBioLabels;
  align: Align;
}) {
  const { event } = item;
  if (!event) return null;

  const place = event.type === 'virtual' ? labels.online : event.location;
  const parts = [event.dateLabel, place].filter(Boolean);
  const badge =
    event.status === 'canceled'
      ? labels.canceled
      : event.status === 'sold_out'
        ? labels.soldOut
        : null;

  if (parts.length === 0 && !badge) return null;

  return (
    <span
      className={clsx(
        'mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-normal opacity-80',
        align.justify,
      )}
    >
      {parts.length > 0 && <span>{parts.join(' · ')}</span>}
      {badge && (
        <span className="rounded-full border border-current px-1.5 py-px text-[11px] font-medium leading-4">
          {badge}
        </span>
      )}
    </span>
  );
}

function ItemImage({ url, className }: { url: string; className?: string }) {
  return (
    <img
      src={getUrlFromCdn(url, { width: 960, height: 540, fit: CDNFitOption.cover })}
      alt=""
      loading="lazy"
      className={clsx('object-cover', className)}
    />
  );
}

function EmailSignup({
  item,
  labels,
  palette,
  signup,
  alignment,
}: {
  item: Extract<LinkInBioItem, { kind: 'email' }>;
  labels: LinkInBioLabels;
  palette: Palette;
  signup: Props['signup'];
  alignment: LinkInBioProfileView['align'];
}) {
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const align = ALIGN[alignment];
  // The dashboard preview sits inside the editor's form, and forms cannot nest.
  const Fields = (signup ? 'form' : 'div') as React.ElementType;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    if (!signup || !item.blockId) return;
    setState('sending');
    try {
      const response = await fetch(signup.url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({
          seller: signup.seller,
          block: item.blockId,
          email: form.get('email'),
          website: form.get('website') || null,
        }),
      });
      setState(response.ok ? 'done' : 'error');
    } catch {
      setState('error');
    }
  };

  return (
    <div
      className={clsx('border px-4 py-4', align.text)}
      style={{
        ...CARD_RADIUS,
        background: palette.surface,
        color: palette.surfaceText,
        borderColor: palette.surfaceBorder,
      }}
    >
      <p className="text-[15px] font-semibold leading-snug">{item.title || labels.emailTitle}</p>
      {state === 'done' ? (
        <p className="mt-2 text-sm" role="status">
          {labels.emailThanks}
        </p>
      ) : (
        <Fields className="mt-3 flex flex-col gap-2 @sm:flex-row" {...(signup ? { onSubmit } : {})}>
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            aria-label={labels.emailPlaceholder}
            placeholder={labels.emailPlaceholder}
            className="cp_form-textinput min-w-0 flex-1 outline-none"
          />
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute -left-[9999px] h-0 w-0 opacity-0"
          />
          <button
            type={signup ? 'submit' : 'button'}
            onClick={signup ? undefined : () => setState('done')}
            disabled={state === 'sending'}
            className="px-4 py-2.5 text-[15px] font-semibold transition-opacity disabled:opacity-60"
            style={{
              ...BUTTON_RADIUS,
              background: palette.surfaceButton,
              color: palette.surfaceButtonText,
            }}
          >
            {labels.emailButton}
          </button>
        </Fields>
      )}
      {state === 'error' && (
        <p className="mt-2 text-sm" role="alert">
          {labels.emailError}
        </p>
      )}
    </div>
  );
}

function ItemLink({
  item,
  labels,
  palette,
  linkTarget,
  alignment,
}: {
  item: Exclude<LinkInBioItem, { kind: 'heading' | 'email' }>;
  labels: LinkInBioLabels;
  palette: Palette;
  linkTarget: '_self' | '_blank';
  alignment: LinkInBioProfileView['align'];
}) {
  const align = ALIGN[alignment];
  const title = item.kind === 'portal' ? item.title || labels.portal : item.title;
  const imageUrl = item.kind === 'link' || item.kind === 'page' ? item.imageUrl : null;
  const target = linkTarget;
  const rel =
    item.kind === 'link' ? LINK_REL : target === '_blank' ? 'noopener noreferrer' : undefined;
  const hasLead = !!imageUrl || (!!item.icon && isBadgeIcon(item.icon)) || item.kind === 'portal';

  if (item.style === 'card' && imageUrl) {
    return (
      <a
        href={item.href}
        target={target}
        rel={rel}
        data-cp-item={item.key}
        data-cp-label={title}
        className="block overflow-hidden border transition-transform active:scale-[0.99]"
        style={{
          ...CARD_RADIUS,
          background: palette.surface,
          color: palette.surfaceText,
          borderColor: palette.surfaceBorder,
        }}
      >
        <ItemImage url={imageUrl} className="aspect-video w-full" />
        <span
          className={clsx(
            'flex flex-col px-4 py-3 text-[15px] font-semibold leading-snug',
            align.items,
            align.text,
          )}
        >
          {title}
          {item.kind === 'page' && <EventLine item={item} labels={labels} align={align} />}
        </span>
      </a>
    );
  }

  return (
    <a
      href={item.href}
      target={target}
      rel={rel}
      data-cp-item={item.key}
      data-cp-label={title}
      className={clsx(
        'relative flex min-h-[52px] items-center py-2.5 text-[15px] font-semibold leading-snug transition-transform active:scale-[0.99]',
        align.justify,
        align.text,
        buttonPadding(alignment, hasLead),
      )}
      style={{ ...BUTTON_RADIUS, background: palette.buttonBackground, color: palette.buttonText }}
    >
      {imageUrl && (
        <ItemImage
          url={imageUrl}
          className="absolute left-1.5 top-1/2 h-10 w-10 -translate-y-1/2 rounded-lg"
        />
      )}
      {!imageUrl && item.icon && isBadgeIcon(item.icon) ? (
        <BadgeIcon icon={item.icon} className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2" />
      ) : (
        !imageUrl &&
        item.kind === 'portal' && (
          <UserCircleIcon
            className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2"
            aria-hidden="true"
          />
        )
      )}
      <span className={clsx('flex flex-col', align.items)}>
        {title}
        {item.kind === 'page' && <EventLine item={item} labels={labels} align={align} />}
      </span>
    </a>
  );
}

export default function LinkInBioView({
  profile,
  items,
  labels,
  showBranding = false,
  affiliateToken = null,
  linkTarget = '_self',
  signup = null,
  theme = null,
  ownsDocument = false,
  className,
}: Props) {
  const palette = getPalette(profile.look);
  const brandColor = profile.brandColor;
  // The document background can only read the theme's variables if they sit on :root.
  const scope = ownsDocument ? ':root' : '.cp_link-in-bio';
  const themeCss = React.useMemo(
    () =>
      getThemeCss({
        theme: mergeTheme(
          theme as Parameters<typeof mergeTheme>[0],
          null,
          { brandColor },
          {
            brandColor: 'fillsUnsetColors',
          },
        ),
        seller: { brandColor },
        checkoutLayout: 'center',
      }).replaceAll(':root', scope),
    [theme, brandColor, scope],
  );
  const css = [scopedDefaults(scope), themeCss, ownsDocument && documentBackground(palette)]
    .filter(Boolean)
    .join('\n');
  const align = ALIGN[profile.align];
  const initial = profile.title.trim().charAt(0).toUpperCase();

  return (
    <div
      className={clsx(
        'cp_link-in-bio @container flex min-h-full w-full justify-center font-sans',
        className,
      )}
      style={
        {
          background: palette.backdrop,
          color: palette.text,
          fontFamily: 'var(--cp_font-body)',
          '--cp_branding-badge-color': palette.text,
        } as React.CSSProperties
      }
    >
      <style dangerouslySetInnerHTML={{ __html: css }} />
      {/* Container queries, not screen width, so the dashboard's scaled desktop preview shows the card too. */}
      <main
        className="flex w-full flex-col @2xl:my-10 @2xl:max-w-[580px] @2xl:rounded-[2rem] @2xl:shadow-xl"
        style={{ background: palette.background }}
      >
        <div className="mx-auto flex w-full max-w-[480px] flex-1 flex-col px-4 pb-8 pt-10 @2xl:pt-12">
          <header className={clsx('flex flex-col', align.items, align.text)}>
            {profile.avatarUrl ? (
              <img
                src={getUrlFromCdn(profile.avatarUrl, {
                  width: 160,
                  height: 160,
                  fit: CDNFitOption.cover,
                })}
                alt=""
                className="size-20 rounded-full bg-white object-cover"
              />
            ) : (
              initial && (
                <span
                  className="flex size-20 items-center justify-center rounded-full text-2xl font-semibold"
                  style={{ background: palette.buttonBackground, color: palette.buttonText }}
                  aria-hidden="true"
                >
                  {initial}
                </span>
              )
            )}
            {profile.title && (
              <h1
                className="mt-4 text-xl leading-tight"
                style={{
                  fontFamily: 'var(--cp_font-heading)',
                  fontWeight: 'var(--cp_font-heading-weight)',
                }}
              >
                {profile.title}
              </h1>
            )}
            {profile.bio && (
              <p
                className="mt-2 whitespace-pre-line text-[15px] leading-relaxed"
                style={{ color: palette.mutedText }}
              >
                {profile.bio}
              </p>
            )}
            {profile.socials.length > 0 && (
              <ul className={clsx('mt-4 flex flex-wrap gap-1', align.justify)}>
                {profile.socials.map(social => (
                  <li key={`${social.platform}:${social.url}`}>
                    <a
                      href={social.url}
                      target={social.url.startsWith('mailto:') ? undefined : '_blank'}
                      rel={LINK_REL}
                      aria-label={labels.social[social.platform]}
                      data-cp-item={`social:${social.platform}`}
                      data-cp-label={labels.social[social.platform]}
                      className="flex h-10 w-10 items-center justify-center rounded-full transition-opacity hover:opacity-70"
                    >
                      <SocialIcon platform={social.platform} className="h-5 w-5" />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </header>

          {items.length > 0 && (
            <ul className="mt-8 flex flex-col gap-3">
              {items.map(item =>
                item.kind === 'heading' ? (
                  <li key={item.key} className="mt-3 first:mt-0">
                    <h2
                      className={clsx('text-sm font-semibold', align.text)}
                      style={{ color: palette.mutedText }}
                    >
                      {item.title}
                    </h2>
                  </li>
                ) : item.kind === 'email' ? (
                  <li key={item.key}>
                    <EmailSignup
                      item={item}
                      labels={labels}
                      palette={palette}
                      signup={signup}
                      alignment={profile.align}
                    />
                  </li>
                ) : (
                  <li key={item.key}>
                    <ItemLink
                      item={item}
                      labels={labels}
                      palette={palette}
                      linkTarget={linkTarget}
                      alignment={profile.align}
                    />
                  </li>
                ),
              )}
            </ul>
          )}

          {showBranding && (
            <div className="mt-auto pt-10">
              <PoweredByCheckoutPage
                variant="pill"
                surface="linkInBio"
                affiliateToken={affiliateToken}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
